import React, { forwardRef, useEffect, useImperativeHandle } from 'react';

import { useDebouncedState } from 'hooks/useDebouncedState';

import BdInput from '../BdInput/BdInput';

export type BdSearchRefAttributes = {
  clear: () => void;
};
type BdSearchProps = {
  /** The placeholder for the input when there is no value */
  placeholder: string;
  /** The value of the search text */
  searchText?: string;
  /** The label of the input */
  labelText?: string;
  /** How many ms to allow user to input text close enough before calling onSearchTextChange */
  debounceDelay?: number;
  /** If the input is loading */
  isLoading?: boolean;
  /** If we only want the bottom border on the input */
  bottomBorderOnly?: boolean;
  /** If the input is disabled */
  disabled?: boolean;
  /** Event that fires the most recent value of the input after the debounce completed */
  onSearchTextChange: (text: string) => void;
  /** Fires the most recent value regardless of debounce */
  onNonDebouncedSearchTextChange?: (text: string) => void;
};
export const BdSearch = forwardRef<BdSearchRefAttributes, BdSearchProps>(
  (
    {
      placeholder,
      searchText: searchTextSeed,
      labelText,
      debounceDelay = 500,
      isLoading = false,
      bottomBorderOnly = false,
      disabled = false,
      onSearchTextChange,
      onNonDebouncedSearchTextChange,
      ...props
    },
    ref
  ) => {
    useImperativeHandle(ref, () => ({
      clear: () => setSearchTextWithoutDebounce(''),
    }));

    const [searchText, searchTextDebounced, setSearchText, setSearchTextWithoutDebounce] =
      useDebouncedState(searchTextSeed || '', debounceDelay);

    useEffect(() => {
      // If the value is changed programatically elsewhere, we need to change the input as well
      setSearchTextWithoutDebounce(searchTextSeed || '');
    }, [searchTextSeed]);

    useEffect(() => {
      onSearchTextChange && onSearchTextChange(searchTextDebounced);
    }, [searchTextDebounced]);

    useEffect(() => {
      onNonDebouncedSearchTextChange && onNonDebouncedSearchTextChange(searchText);
    }, [searchText]);

    return (
      <BdInput
        value={searchText}
        placeholder={placeholder}
        onChange={setSearchText}
        isLoading={isLoading}
        iconName="icon-search"
        bottomBorderOnly={bottomBorderOnly}
        disabled={disabled}
        labelText={labelText}
        {...props}
      />
    );
  }
);
